import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronsLeft, ChevronsRight, Info } from 'lucide-react';
import { PermissionGate, usePermissions } from '../../context/PermissionContext';
import { ADMIN_NAV, findCategoryForTab } from './adminNav';
import { SidebarQuickSearch } from './SidebarQuickSearch';
import { AdminNotificationsBell } from './AdminNotificationsBell';
import { ReleaseBadge } from './ReleaseBadge';

const Badge = ({ count }) =>
  count > 0 ? (
    <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-badge">
      {count > 9 ? '9+' : count}
    </span>
  ) : null;

const Category = ({ cat, activeTab, onSelect, collapsed, open, onToggle, badges, canView }) => {
  const Icon = cat.icon;
  const isActiveCat = cat.items.some((i) => i.value === activeTab);
  const items = cat.items.filter((i) => !i.can || canView(i.can));
  if (!items.length) return null;
  const single = items.length === 1;

  const headerClick = () => (single ? onSelect(items[0].value) : onToggle());

  return (
    <div className="mb-1" data-testid={cat.testId || `${cat.id}-category`}>
      <button
        type="button"
        onClick={headerClick}
        title={collapsed ? cat.label : undefined}
        className={`w-full flex items-center gap-3 px-3 h-10 rounded-lg text-sm font-medium transition-colors ${
          isActiveCat ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:bg-neutral-100'
        } ${collapsed ? 'justify-center px-0' : ''}`}
        data-testid={`nav-cat-${cat.id}`}
        aria-expanded={!single ? open : undefined}
      >
        <Icon className="w-4 h-4 shrink-0" />
        {!collapsed && <span className="truncate">{cat.label}</span>}
        {!collapsed && !single && <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />}
        {!collapsed && single && badges[items[0].badge] > 0 && <Badge count={badges[items[0].badge]} />}
      </button>

      {!single && (collapsed ? isActiveCat : open) && (
        <ul className={`mt-1 space-y-0.5 ${collapsed ? '' : 'ml-3 pl-3 border-l border-neutral-200'}`}>
          {items.map((item) => {
            const ItemIcon = item.icon;
            const active = activeTab === item.value;
            return (
              <li key={item.value}>
                <button
                  type="button"
                  onClick={() => onSelect(item.value)}
                  title={collapsed ? item.label : undefined}
                  className={`w-full flex items-center gap-2.5 px-3 h-9 rounded-md text-[13px] transition-colors ${
                    active ? 'bg-amber-50 text-amber-800 font-medium' : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  } ${collapsed ? 'justify-center px-0' : ''}`}
                  data-testid={item.testId || `nav-item-${item.value}`}
                  aria-current={active ? 'page' : undefined}
                >
                  <ItemIcon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-amber-600' : 'text-neutral-400'}`} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {!collapsed && item.info && (
                    <span className="group relative ml-auto shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Info className="w-3 h-3 text-neutral-300 hover:text-neutral-500 transition-colors cursor-help" />
                      <span className="invisible group-hover:visible absolute left-6 bottom-0 z-50 w-52 px-3 py-2 text-[11px] leading-relaxed text-neutral-700 bg-white border border-neutral-200 rounded-lg shadow-lg pointer-events-none">
                        {item.info}
                      </span>
                    </span>
                  )}
                  {!collapsed && <Badge count={badges[item.badge]} />}
                  {collapsed && badges[item.badge] > 0 && <span className="absolute w-2 h-2 rounded-full bg-red-500 translate-x-3 -translate-y-2" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const RECENT_KEY = 'admin_recent_tabs';
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; } };

const RecentPages = ({ activeTab, onSelect, collapsed }) => {
  const [recent, setRecent] = useState(readRecent);
  useEffect(() => {
    if (!activeTab) return;
    setRecent((prev) => {
      const next = [activeTab, ...prev.filter((t) => t !== activeTab)].slice(0, 4);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      return next;
    });
  }, [activeTab]);
  const items = recent.filter((t) => t !== activeTab).slice(0, 3)
    .map((t) => { const cat = findCategoryForTab(t); const item = cat?.items.find((i) => i.value === t); return item ? { ...item, category: cat.label } : null; })
    .filter(Boolean);
  if (!items.length || collapsed) return null;
  return (
    <div className="px-3 pt-2 pb-1" data-testid="sidebar-recent">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-1">Recent</p>
      <div className="flex flex-wrap gap-1">
        {items.map((i) => {
          const Icon = i.icon;
          return (
            <button key={i.value} type="button" onClick={() => onSelect(i.value)} title={`${i.category} › ${i.label}`}
              className="inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full border border-neutral-200 bg-white text-[11px] text-neutral-600 hover:border-amber-400 hover:text-amber-800 transition-colors"
              data-testid={`sidebar-recent-${i.value}`}>
              <Icon className="w-3 h-3 text-neutral-400" />{i.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const AdminSidebar = ({ activeTab, onSelect, onSelectColor, badges = {}, collapsed, onToggleCollapsed }) => {
  const { canView } = usePermissions();
  const [openIds, setOpenIds] = useState(() => {
    const cat = findCategoryForTab(activeTab);
    return cat ? [cat.id] : ['home'];
  });

  useEffect(() => {
    const cat = findCategoryForTab(activeTab);
    if (cat) setOpenIds((prev) => (prev.includes(cat.id) ? prev : [...prev, cat.id]));
  }, [activeTab]);

  const toggle = (id) => setOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <nav className={`flex flex-col h-full ${collapsed ? 'w-16' : 'w-60'} transition-[width] duration-200`} data-testid="admin-sidebar" aria-label="Admin sections">
      <div className={`flex items-center ${collapsed ? 'flex-col justify-center gap-0.5 h-auto py-1.5' : 'justify-between h-12'} px-3 border-b border-neutral-200`}>
        {!collapsed && <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400 flex-1">Admin</span>}
        <AdminNotificationsBell onSelectTab={onSelect} collapsed={collapsed} />
        <button
          type="button"
          onClick={onToggleCollapsed}
          className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
        </button>
      </div>
      <SidebarQuickSearch onSelectTab={onSelect} onSelectColor={onSelectColor} collapsed={collapsed} onExpand={() => collapsed && onToggleCollapsed?.()} />
      <RecentPages activeTab={activeTab} onSelect={onSelect} collapsed={collapsed} />
      <div className="flex-1 overflow-y-auto p-2">
        {ADMIN_NAV.map((cat) => {
          const node = (
            <Category
              key={cat.id}
              cat={cat}
              activeTab={activeTab}
              onSelect={onSelect}
              collapsed={collapsed}
              open={openIds.includes(cat.id)}
              onToggle={() => toggle(cat.id)}
              badges={badges}
              canView={canView}
            />
          );
          return cat.module ? (
            <PermissionGate key={cat.id} module={cat.module} level="view" fallback={null}>{node}</PermissionGate>
          ) : node;
        })}
      </div>
      <ReleaseBadge collapsed={collapsed} />
    </nav>
  );
};

export default AdminSidebar;
